import { useCallback, useEffect, useState } from 'react';
import { getTemplatePreference, saveTemplatePreference } from './api';
import { DEFAULT_ACCENT_KEY, DEFAULT_TEMPLATE_FAMILY, type ResumeTemplateFamily } from './resumeTemplateRegistry';

/**
 * The user's template + accent preference as page/panel state (TEMPLATES-P2,
 * Decision 9). Loads once on mount (GET /account/template-preference); a
 * tap on a TemplatePicker card or accent swatch calls `setFamily`/`setAccent`,
 * which optimistically updates local state AND fires the PATCH in the
 * background — the picker never blocks on a round-trip before reflecting
 * the user's tap (the same "instant, no spinner-per-tap" feel the written/
 * raw cut TOGGLE in useCorpusResume.ts already has, applied to a persisted
 * preference instead of a pure display flip).
 *
 * A failed load degrades to the documented defaults (never a blank/broken
 * picker) — the picker is still fully usable, it just starts from
 * ats-plain/sage-teal instead of a previously-saved choice, and a save
 * failure surfaces via `saveError` without reverting the optimistic UI (the
 * user's tap is still honored on screen; only the PERSISTED preference may
 * lag, and the next successful save corrects it).
 */
export function useTemplatePreference() {
  const [family, setFamilyState] = useState<string>(DEFAULT_TEMPLATE_FAMILY);
  const [accentKey, setAccentKeyState] = useState<string>(DEFAULT_ACCENT_KEY);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTemplatePreference()
      .then((r) => {
        if (cancelled) return;
        setFamilyState(r.family);
        setAccentKeyState(r.accent);
      })
      .catch(() => {
        // Degrades to the documented defaults already in state — never a
        // blocked/blank picker just because the preference read failed.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setFamily = useCallback((next: ResumeTemplateFamily) => {
    setFamilyState(next);
    setSaveError(null);
    saveTemplatePreference({ family: next }).catch((err) => {
      setSaveError(err instanceof Error ? err.message : String(err));
    });
  }, []);

  const setAccent = useCallback((next: string) => {
    setAccentKeyState(next);
    setSaveError(null);
    saveTemplatePreference({ accentKey: next }).catch((err) => {
      setSaveError(err instanceof Error ? err.message : String(err));
    });
  }, []);

  return { family, accentKey, loading, saveError, setFamily, setAccent };
}
